import React, { useState, useMemo, useEffect } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingFocusManager,
} from "@floating-ui/react";
import styles from "./EmojiPicker.module.scss";
import { InputField } from "../InputField";
import { Button } from "../Button/button";
import type { Emoji } from "./emojiList";

interface EmojiPickerProps {
  onSelect: (emoji: string | null) => void;
  selectedEmoji?: string | null;
  dark?: boolean;
}

export const EmojiPicker: React.FC<EmojiPickerProps> = ({
  onSelect,
  selectedEmoji,
  dark = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [emojis, setEmojis] = useState<Emoji[]>([]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(10), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  // Lazy load emoji list only when picker is opened
  useEffect(() => {
    if (isOpen && emojis.length === 0) {
      import("./emojiList").then((m) => setEmojis(m.EMOJI_LIST));
    }
  }, [isOpen, emojis.length]);

  const filteredEmojis = useMemo(() => {
    if (!search) return emojis;
    const lowerSearch = search.toLowerCase();
    return emojis.filter(
      (e) =>
        e.name.toLowerCase().includes(lowerSearch) ||
        e.tags.some((tag) => tag.toLowerCase().includes(lowerSearch)),
    );
  }, [search, emojis]);

  const handleSelect = (char: string | null) => {
    onSelect(char);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div className={`${styles.container} ${dark ? styles.dark : ""}`}>
      <button
        ref={refs.setReference}
        type="button"
        className={styles.trigger}
        title="Choose an emoji"
        aria-label="Choose an emoji"
        {...getReferenceProps({
          onClick(event) {
            event.stopPropagation();
          },
        })}
      >
        {selectedEmoji || <div className={styles.emojiPlaceholder}></div>}
      </button>

      <FloatingPortal>
        {isOpen && (
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className={`${styles.popover} ${dark ? styles.dark : ""}`}
              {...getFloatingProps()}
            >
              <div className={styles.header}>
                <InputField
                  autoFocus
                  compact
                  icon="search"
                  placeholder="Search..."
                  aria-label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={styles.searchInput}
                  dark={dark}
                />
                <Button
                  circular
                  blue
                  secondary
                  icon="delete"
                  className={styles.clearBtn}
                  onClick={() => handleSelect(null)}
                  title="Clear emoji"
                  aria-label="Clear emoji"
                />
              </div>
              <div className={styles.grid}>
                {filteredEmojis.length > 0 ? (
                  filteredEmojis.map((emoji) => (
                    <button
                      key={emoji.char}
                      type="button"
                      className={`${styles.emojiBtn} ${selectedEmoji === emoji.char ? styles.selected : ""}`}
                      onClick={() => handleSelect(emoji.char)}
                      title={emoji.name}
                      aria-label={`Select ${emoji.char} (${emoji.name})`}
                    >
                      {emoji.char}
                    </button>
                  ))
                ) : (
                  <div className={styles.noResults}>No emojis found</div>
                )}
              </div>
            </div>
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </div>
  );
};
